import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/admin/ui";
import { StatusBadge } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { formatDate, waLink } from "@/lib/format";

const BlockForm = ({ date, trips, onChanged }) => {
  const [scope, setScope] = useState("all");
  const [until, setUntil] = useState(date);
  const [reason, setReason] = useState("");
  useEffect(() => { setUntil(date); }, [date]);
  const block = useMutation({
    mutationFn: () => api.post("/admin/blocked-dates", { date, end_date: until >= date ? until : date, trip_id: scope === "all" ? null : scope, reason }),
    onSuccess: (r) => { toast.success(`${r.data.length} tanggal ditutup`); setReason(""); onChanged(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  return (
    <div className="space-y-4 border-t border-white/10 pt-5">
      <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Tutup tanggal</p>
      <Field label="Berlaku untuk">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="admin-block-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua trip</SelectItem>
            {trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Sampai tanggal"><Input type="date" min={date} value={until} onChange={(e) => setUntil(e.target.value)} data-testid="admin-block-end-date" /></Field>
      <Field label="Alasan (opsional)"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="cth. Libur, cuaca buruk" data-testid="admin-block-reason" /></Field>
      <Button className="w-full" onClick={() => block.mutate()} disabled={block.isPending} data-testid="admin-block-submit"><Lock /> Tutup tanggal</Button>
    </div>
  );
};

export const DayPanel = ({ date, entry, trips, onChanged }) => {
  const unblock = useMutation({
    mutationFn: (id) => api.delete(`/admin/blocked-dates/${id}`),
    onSuccess: () => { toast.success("Tanggal dibuka kembali"); onChanged(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  if (!date) return <p className="text-sm text-neutral-500">Pilih tanggal di kalender.</p>;
  const orders = entry?.orders || [];
  const blocked = entry?.blocked || [];
  return (
    <div className="space-y-5" data-testid="admin-day-panel">
      <p className="font-display text-2xl capitalize">{formatDate(date, { weekday: "long" })}</p>
      {orders.length === 0 && blocked.length === 0 && <p className="text-sm text-emerald-300">Tanggal masih kosong.</p>}
      {orders.map((o) => (
        <div key={o.id} className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid={`admin-day-order-${o.id}`}>
          <div className="flex items-start justify-between gap-3">
            <p className="font-mono text-sm text-gold-light">{o.code}</p>
            <StatusBadge status={o.status} />
          </div>
          <p className="mt-2 text-sm">{o.trip_name} · <span className="text-neutral-500">{o.package_name}</span></p>
          <p className="text-xs text-neutral-500">{o.customer_name} · {o.participants} pax · <a href={waLink(o.phone)} target="_blank" rel="noreferrer" className="hover:text-gold">{o.phone}</a></p>
        </div>
      ))}
      {blocked.map((b) => (
        <div key={b.id} className="flex items-center justify-between gap-3 rounded-xl border border-neutral-700 bg-neutral-800/50 p-4" data-testid={`admin-day-block-${b.id}`}>
          <div>
            <p className="text-sm">Ditutup · {b.trip_name || "Semua trip"}</p>
            {b.reason && <p className="text-xs text-neutral-500">{b.reason}</p>}
          </div>
          <Button size="sm" variant="outline" onClick={() => unblock.mutate(b.id)} data-testid={`admin-unblock-${b.id}`}><Unlock /> Buka</Button>
        </div>
      ))}
      <BlockForm date={date} trips={trips} onChanged={onChanged} />
    </div>
  );
};
